import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useIterateModifiers from '@/hooks/useIterateModifiers';
import { goToWhereToBuy } from '@/utils/Common/Redirects';
import { isEcommerce, isTrue } from '@/utils/Common/Booleans';
import { hasImageSizes } from '@/utils/Common/Utils';
import GTM from '@/utils/Controllers/GTM/Constants';

// components
import Button from '@/components/atoms/Button';
import ButtonTextIcon from '@/components/atoms/ButtonTextIcon';
import ButtonLink from '@/components/atoms/ButtonLink';

const WishlistProduct = ({
  modifiers,
  product,
  hasDelete,
  addEntry,
  removeEntry,
  notifyEntry,
  position,
}) => {
  const { t } = useTranslation();
  const modifiersString = useIterateModifiers('dln-wishProd--', modifiers);
  const hasStock = (product.stock && product.stock.stockLevel > 0) || false;

  const outOfStockClass =
    !hasStock && isEcommerce ? 'dln-wishProd--outOfStock' : '';
  const isNotified = product.stockNotificationAlreadyCreated;
  const alreadyNotifiedClass = isNotified ? 'action-done' : '';
  const productUrl = `${DLG.config.encodedContextPath}/${product.url}`;

  const hasOldPrice =
    isTrue(DLG.config.oldPriceEnabled) &&
    product.oldPrice &&
    product.oldPrice.formattedValue;

  return (
    <div className={`dln-wishProd ${modifiersString} ${outOfStockClass}`}>
      <input
        type='hidden'
        id={`js-gtm-product-click-${product.code}`}
        className='js-gtm-product-impression'
        value={product.gtmInfo}
        data-product-position={position}
        data-list-name={GTM.LIST_NAME.WISHLIST}
        data-product-dimension1={
          product.stock && product.stock.stockLevelStatus
        }
        data-product-price={product.price && product.price.value}
      />
      <a
        className='dln-wishProd__imgLink js-gtm-product-click'
        id={product.code}
        href={productUrl}
      >
        {hasImageSizes(product.dlgImage) && (
          <picture className='dln-image dln-wishProd__imgWrap'>
            <source srcSet={product.dlgImage.l.url} media='(min-width:901px)' />
            <source srcSet={product.dlgImage.m.url} media='(min-width:600px)' />
            <img
              className='dln-image__image'
              src={product.dlgImage.s.url}
              alt={product.dlgImage.altText}
            />
          </picture>
        )}
      </a>
      <div className='dln-wishProd__data'>
        <a
          className='dln-wishProd__name js-gtm-product-click'
          id={product.code}
          href={productUrl}
        >
          {product.name}
        </a>
        {!hasStock && isEcommerce && (
          <div className='dln-wishProd__priceWrap dln-wishProd__priceWrap--outOfStock'>
            <p className='dln-wishProd__outOfStock'>
              {t('product.out.of.stock.label')}
            </p>
          </div>
        )}
        <div className='dln-wishProd__priceWrap'>
          {hasOldPrice && (
            <p className='dln-wishProd__price dln-wishProd__price--discounted'>
              {product.oldPrice.formattedValue}
            </p>
          )}
          {product.price && product.price.formattedValue && (
            <p
              className={`dln-wishProd__price${
                hasOldPrice ? 'Discounted' : ''
              }`}
            >
              {product.price.formattedValue}
            </p>
          )}
        </div>
        {hasDelete ? (
          <ButtonLink
            text={t('text.delete')}
            onClick={() => removeEntry(product.code)}
          />
        ) : null}

        {!isEcommerce && (
          <Button onClick={goToWhereToBuy} text={t('where.to.buy.cta')} />
        )}

        {isEcommerce && hasStock && (
          <ButtonTextIcon
            onClick={() => addEntry(product.code)}
            text={t('add.to.cart.label')}
            glyph='8'
          />
        )}

        {isEcommerce && !hasStock && (
          <Button
            modifiers={['stroked', 'action-button', alreadyNotifiedClass]}
            glyph={isNotified ? 'H' : ''}
            text={t(
              `wishlist:wishlist.page.${
                isNotified ? 'already.notified' : 'notify.me'
              }.label`,
            )}
            onClick={() => !isNotified && notifyEntry(product.code)}
          />
        )}
      </div>
    </div>
  );
};

WishlistProduct.propTypes = {
  modifiers: PropTypes.arrayOf(PropTypes.string),
  product: PropTypes.shape().isRequired,
  hasDelete: PropTypes.bool,
  addEntry: PropTypes.func.isRequired,
  removeEntry: PropTypes.func,
  notifyEntry: PropTypes.func.isRequired,
  position: PropTypes.number,
};

WishlistProduct.defaultProps = {
  modifiers: [],
  hasDelete: true,
  removeEntry: null,
  position: 1,
};

export default WishlistProduct;
